// Reading a YouTube link, and building a player URL from it.
//
// # WHY THIS IS A MODULE AND NOT TWO INLINE REGEXES
//
// It used to be two inline regexes, and they disagreed. The modal's required
// exactly 11 id characters but had no branch for `/shorts/`, which the corpus
// does contain. train.js's left its dots unescaped — so `youtu.be` also matched
// `youtuXbe` — took `[^#&?]*` for the id, and matched a bare `v/` anywhere in
// the string.
//
// The result: the same link played on one screen and reported "no demo video is
// available" on another, so which screen you opened an exercise from decided
// whether it had a demo at all.
//
// One parser, one answer, tested on its own.
//
// (The old expressions are described rather than quoted here on purpose: both
// end in `*/`, which closes a block comment and breaks the file.)

// A YouTube id is exactly 11 characters of [A-Za-z0-9_-]. Anchoring on the
// length is what stops a trailing path segment or query being swallowed into
// the id — the failure that produced embed URLs ending in "/null".
const ID = '([A-Za-z0-9_-]{11})';

const PATTERNS = [
  // youtu.be/<id>
  new RegExp(`^(?:https?://)?(?:www\\.)?youtu\\.be/${ID}`),
  // youtube.com/watch?v=<id>, with the parameter anywhere in the query
  new RegExp(`^(?:https?://)?(?:www\\.)?(?:m\\.)?youtube(?:-nocookie)?\\.com/watch\\?(?:.*&)?v=${ID}`),
  // youtube.com/embed/<id>, /v/<id>, /shorts/<id>, /live/<id>
  new RegExp(`^(?:https?://)?(?:www\\.)?(?:m\\.)?youtube(?:-nocookie)?\\.com/(?:embed|v|shorts|live)/${ID}`),
];

/**
 * The video id in a link, or null.
 *
 * Null is a real answer, not a failure: the library carries exercises with no
 * demo, and "0" appears as a placeholder in some exports. A caller renders "no
 * demo available" rather than a broken player — see exerciseVideoLink, which
 * strips the placeholder before this ever sees it.
 */
export function videoIdFrom(url) {
  const text = String(url ?? '').trim();
  if (text === '' || text === '0') return null;

  for (const pattern of PATTERNS) {
    const match = text.match(pattern);
    if (match) return match[1];
  }
  return null;
}

/**
 * The player URL for a link, or null.
 *
 * # THE HOST
 *
 * youtube-nocookie.com is YouTube's privacy-enhanced mode: it holds back
 * cookies until the viewer presses play, and any ads it does serve are
 * non-personalised and are not used to target that person elsewhere.
 *
 * It does NOT remove ads. Nothing here can — whether a video carries
 * advertising is the video owner's monetisation setting, decided server-side,
 * and there is no player parameter that turns it off. Blocking the requests
 * would breach YouTube's terms. Genuinely ad-free playback means hosting the
 * clips ourselves, which is a different and much larger piece of work.
 *
 * # THE PARAMETERS
 *
 *   rel=0             related videos restricted to the same channel. Since 2018
 *                     this can no longer suppress them entirely.
 *   playsinline=1     plays in place on iOS instead of seizing the whole screen,
 *                     which would undo the point of a modal.
 *   iv_load_policy=3  no annotation overlays.
 *
 * modestbranding is deliberately NOT set: Google deprecated it in August 2023
 * and it now has no effect, so setting it would only imply a guarantee that is
 * not being made.
 */
export function embedUrlFrom(url) {
  const id = videoIdFrom(url);
  if (!id) return null;
  return `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1&iv_load_policy=3`;
}

/**
 * The YouTube page for a link, or null.
 *
 * # WHY A SECOND URL BUILDER
 *
 * An embed can be refused while the video is perfectly alive. A video owner can
 * disable embedding, and then oEmbed answers 403 and our iframe renders an empty
 * box — indistinguishable, to the athlete, from a demo we simply do not have.
 * Five shipped movements are in exactly that state.
 *
 * Nothing client-side can detect it: a cross-origin iframe that refuses to load
 * fires no error we are allowed to read. So the modal offers this alongside the
 * player unconditionally, rather than trying to notice the failure and react to
 * it. It costs one line of chrome on the videos that do embed, and it is the
 * only route to the ones that do not.
 *
 * youtube.com, not youtube-nocookie.com: this is a link the athlete follows to
 * YouTube itself, where the nocookie host has no meaning.
 */
export function watchUrlFrom(url) {
  const id = videoIdFrom(url);
  if (!id) return null;
  return `https://www.youtube.com/watch?v=${id}`;
}

/**
 * The demo link on an exercise, or '' if it has none.
 *
 * # WHY THIS LIVES BESIDE THE PARSER
 *
 * There were three of these, and they disagreed — the same divergence the
 * parser above was written to end, one step earlier in the pipeline.
 * exerciseVideoLink (the library screen) trimmed and stripped the "0"
 * placeholder but read one key spelling; videoLinkOf (the programme editor)
 * read both spellings but neither trimmed nor stripped; and the logger read
 * `exercise.youTubeLink` raw. A "0" is truthy, so the logger rendered a Demo
 * button that opened a modal with no video in it, on an exercise the library
 * screen correctly showed as having none.
 *
 * # THE TWO SPELLINGS
 *
 * The API is genuinely inconsistent: the exercise library serialises
 * `youtubeLink` (ExerciseDBResponse) while a session's exercise entry
 * serialises `youTubeLink` (ExerciseEntryResponse). The entry's spelling wins
 * when it carries one — the entry's snapshot is what the coach prescribed, and
 * the library's copy is what it happens to say now.
 *
 * `||` rather than `??` on purpose: an empty entry snapshot is not an assertion
 * that the movement has no demo, it is a prescription written when the library
 * had none to copy. Falling through to the library's spelling can only turn a
 * missing demo into a playable one.
 *
 * "0" appears as a placeholder in some exports and means absent, not a video.
 */
export function exerciseVideoLink(exercise) {
  const link = String(exercise?.youTubeLink || exercise?.youtubeLink || '').trim();
  if (link === '' || link === '0') return '';
  return link;
}
